import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { 
  Package, 
  Loader2,
  ArrowRight,
  MapPin,
  XCircle,
  Eye,
  Zap
} from "lucide-react";

export default function MyDeliveries() {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);
  const navigate = useNavigate();

  const fetchDeliveries = useCallback(async () => {
    try {
      const response = await api.get("/local-delivery/my");
      setDeliveries(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch deliveries:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  const handleCancel = async (deliveryId, delivery) => {
    const isPaid = delivery.payment_method === 'online' && delivery.payment_status === 'paid';
    const hasPartner = delivery.status === 'confirmed';
    
    let confirmMessage = "Are you sure you want to cancel this delivery?";
    if (isPaid) {
      if (hasPartner) {
        confirmMessage += "\n\nRefund Policy: 90% refund (₹" + (delivery.final_price * 0.9).toFixed(2) + ") - 10% cancellation fee applies as a partner was assigned.";
      } else {
        confirmMessage += "\n\nFull refund of ₹" + delivery.final_price + " will be processed.";
      }
    }
    
    if (!window.confirm(confirmMessage)) return;
    
    setCancelling(deliveryId);
    try {
      const response = await api.post(`/local-delivery/${deliveryId}/cancel`);
      const data = response.data;
      
      if (data.refund_details) {
        const { refund_amount, refund_method, partner_compensation } = data.refund_details;
        let message = `Delivery cancelled. Refund of ₹${refund_amount} `;
        message += refund_method === 'wallet' ? 'added to your wallet.' : 'will be processed via Razorpay.';
        if (partner_compensation) {
          message += ` (Partner compensated ₹${partner_compensation})`;
        }
        toast.success(message);
      } else {
        toast.success("Delivery cancelled successfully");
      }
      fetchDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel delivery");
    } finally {
      setCancelling(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'picked_up': return 'bg-purple-100 text-purple-800';
      case 'in_transit': return 'bg-indigo-100 text-indigo-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const canCancel = (status) => {
    return ['pending', 'confirmed'].includes(status);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tight">My Deliveries</h1>
        <Link to="/local-delivery">
          <Button className="rounded-none bg-accent text-accent-foreground" data-testid="new-delivery-btn">
            <Zap className="w-4 h-4 mr-2" />
            Book New Delivery
          </Button>
        </Link>
      </div>

      {deliveries.length === 0 ? (
        <div className="text-center py-10 border border-border">
          <Package className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground mb-4">No deliveries yet</p>
          <Link to="/local-delivery">
            <Button className="rounded-none" data-testid="book-first-delivery-btn">
              Book Your First Delivery
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {deliveries.map((delivery) => (
            <div key={delivery.id} className="border border-border p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-3">
                  {/* Route */}
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-500" />
                    <span className="font-medium">{delivery.pickup_address || delivery.pickup_points?.[0]?.address}</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <MapPin className="w-4 h-4 text-red-500" />
                    <span className="font-medium">{delivery.drop_address || delivery.drop_points?.[0]?.address}</span>
                  </div>
                  
                  {/* Details */}
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span>ID: {delivery.id}</span>
                    <span>Vehicle: {delivery.vehicle_type}</span>
                    <span>₹{delivery.final_price || delivery.estimated_price}</span>
                  </div>
                </div>

                {/* Status & Actions */}
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-3 py-1 text-xs font-medium uppercase ${getStatusColor(delivery.status)}`}>
                    {delivery.status.replace('_', ' ')}
                  </span>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none"
                      onClick={() => navigate(`/track/${delivery.id}`)}
                      data-testid={`track-${delivery.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Track
                    </Button>
                    
                    {canCancel(delivery.status) && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="rounded-none"
                        onClick={() => handleCancel(delivery.id, delivery)}
                        disabled={cancelling === delivery.id}
                        data-testid={`cancel-${delivery.id}`}
                      >
                        {cancelling === delivery.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 mr-1" />
                            Cancel
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
